import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { OFFER_PRIORITIES, ACTIVE_STATES, slug } from "./constants";

const empty = () => ({
  name: "", offer_type: "", description: "", target_audience: "", price_display: "",
  conversion_goal: "", landing_page_url: "", cta_text: "", cta_keyword: "",
  priority: "Secondary", status: "Active",
});

export default function Offers() {
  const [offers, setOffers] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/marketing/offers").then((r) => setOffers(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("new") === "1") {
      openCreate();
      params.delete("new");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const openCreate = () => { setEditingId(null); setForm(empty()); setOpen(true); };
  const openEdit = (o) => { setEditingId(o.id); setForm({ ...empty(), ...o }); setOpen(true); };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Offer name is required"); return; }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/marketing/offers/${editingId}`, form); toast.success("Offer updated"); }
      else { await api.post("/marketing/offers", form); toast.success("Offer created"); }
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (o) => {
    try {
      await api.delete(`/marketing/offers/${o.id}`);
      toast.success("Offer deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (offers === null)
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;

  return (
    <div data-testid="offers-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold">Offers</h2>
        <Button onClick={openCreate} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="add-offer-button">
          <Plus className="h-4 w-4 mr-2" /> Add Offer
        </Button>
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-widest text-gray-500 font-semibold">
          <div className="col-span-4">Offer</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Priority</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {offers.length === 0 && <div className="px-6 py-10 text-center text-gray-400 text-sm">No offers yet.</div>}
        {offers.map((o) => (
          <div key={o.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 border-b border-gray-100 items-center" data-testid={`offer-row-${slug(o.name)}`}>
            <div className="md:col-span-4">
              <p className="font-medium text-sm text-gray-900">{o.name}</p>
              <p className="text-xs text-gray-400 font-mono mt-0.5">Keyword: {o.cta_keyword || "—"}</p>
            </div>
            <div className="md:col-span-2 text-sm text-gray-500">{o.offer_type || "—"}</div>
            <div className="md:col-span-2">
              <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold bg-[#F3E8FF] text-[#6B21A8]">{o.priority}</span>
            </div>
            <div className="md:col-span-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${o.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{o.status}</span>
            </div>
            <div className="md:col-span-2 md:text-right flex md:justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => openEdit(o)} data-testid={`edit-offer-${slug(o.name)}`} className="border-gray-200 hover:border-[#581C87] hover:text-[#581C87]"><Pencil className="h-3.5 w-3.5" /></Button>
              <Button variant="outline" size="sm" onClick={() => del(o)} data-testid={`delete-offer-${slug(o.name)}`} className="border-gray-200 hover:border-red-500 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="offer-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit Offer" : "Add Offer"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Offer Name *</Label><Input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="offer-name-input" className="focus-visible:ring-[#581C87]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Offer Type</Label><Input value={form.offer_type} onChange={(e) => upd("offer_type", e.target.value)} placeholder="Workshop" data-testid="offer-type-input" className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>Primary Conversion Goal</Label><Input value={form.conversion_goal} onChange={(e) => upd("conversion_goal", e.target.value)} placeholder="Enrollment" data-testid="offer-goal-input" className="focus-visible:ring-[#581C87]" /></div>
            </div>
            <div className="space-y-2"><Label>Short Description</Label><Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Target Audience</Label><Input value={form.target_audience} onChange={(e) => upd("target_audience", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>Price Display (optional)</Label><Input value={form.price_display} onChange={(e) => upd("price_display", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
            </div>
            <div className="space-y-2"><Label>Landing-page URL</Label><Input value={form.landing_page_url} onChange={(e) => upd("landing_page_url", e.target.value)} placeholder="https://..." data-testid="offer-url-input" className="focus-visible:ring-[#581C87]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>CTA Text</Label><Input value={form.cta_text} onChange={(e) => upd("cta_text", e.target.value)} className="focus-visible:ring-[#581C87]" /></div>
              <div className="space-y-2"><Label>CTA Keyword</Label><Input value={form.cta_keyword} onChange={(e) => upd("cta_keyword", e.target.value)} data-testid="offer-keyword-input" className="focus-visible:ring-[#581C87]" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => upd("priority", v)}>
                  <SelectTrigger data-testid="offer-priority-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                  <SelectContent>{OFFER_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => upd("status", v)}>
                  <SelectTrigger data-testid="offer-status-select" className="focus:ring-[#581C87]"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIVE_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="offer-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Save Changes" : "Create Offer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
